import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, ArrowLeft, AlertCircle, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ["Persoonlijk", "Lichaam", "Doel", "Gezondheid", "Toestemming"];

function ProgressBar({ current, total }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < current ? "bg-[#C9A84C]" : i === current ? "bg-[#C9A84C]/50" : "bg-white/8"}`} />
      ))}
    </div>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <span className="text-red-400 text-xs mt-1 flex items-center gap-1"><AlertCircle size={11} />{msg}</span>;
}

function InputField({ label, required, error, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">{label}{required && " *"}</span>
      {children}
      <FieldError msg={error} />
    </label>
  );
}

export default function IntakePage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [form, setForm] = useState({
    voornaam: "", achternaam: "", telefoon: "", geboortedatum: "", geslacht: "",
    gewicht: "", lengte: "",
    doel: "", trainingsdagen: 3, weekschema: "", ervaring: "", sportschool: "",
    specifiek_doel: "",
    klachten: "", mentaal: "", medicatie: "",
    toestemming_data: false, toestemming_medisch: false,
  });

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: undefined }));
  };

  useEffect(() => {
    fetch(`${API}/api/orders/${orderId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) { navigate("/"); return; }
        setOrder(d);
        if (d.intake) setAlreadySubmitted(true);
        if (d.status === "aanvraag_ontvangen") setAlreadySubmitted(false);
      });
  }, [orderId, navigate]);

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.voornaam.trim()) e.voornaam = "Verplicht";
      if (!form.achternaam.trim()) e.achternaam = "Verplicht";
      if (!form.email && !order?.email) e.email = "Verplicht";
      if (!form.geboortedatum) e.geboortedatum = "Verplicht";
      if (!form.geslacht) e.geslacht = "Verplicht";
    }
    if (step === 1) {
      if (!form.gewicht) e.gewicht = "Verplicht";
      if (!form.lengte) e.lengte = "Verplicht";
    }
    if (step === 2) {
      if (!form.doel) e.doel = "Verplicht";
      if (!form.ervaring) e.ervaring = "Verplicht";
    }
    if (step === 4) {
      if (!form.toestemming_data) e.toestemming_data = "Toestemming verplicht";
      if (!form.toestemming_medisch) e.toestemming_medisch = "Toestemming verplicht";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (step < STEPS.length - 1) setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/orders/${orderId}/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, trainingsdagen: Number(form.trainingsdagen) }),
      });
      if (res.ok) setDone(true);
      else {
        const d = await res.json();
        setErrors({ submit: d.detail || "Er ging iets mis." });
      }
    } catch {
      setErrors({ submit: "Er ging iets mis, probeer opnieuw." });
    }
    setSubmitting(false);
  };

  if (!order) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="text-zinc-600 text-sm">Laden...</div>
    </div>
  );

  if (alreadySubmitted) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <CheckCircle size={40} className="text-[#C9A84C] mx-auto mb-4" />
        <h2 className="text-white font-bold text-xl mb-2">Intake al ingediend</h2>
        <p className="text-zinc-500 text-sm">Je intake is al ontvangen. We zijn aan de slag met je schema!</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <div className="text-center max-w-md animate-fadeIn">
        <div className="w-20 h-20 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={36} className="text-[#C9A84C]" />
        </div>
        <h2 className="text-white font-bold text-2xl mb-3">Intake ontvangen!</h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-6">
          Bedankt {form.voornaam}! We gaan aan de slag met je persoonlijk schema.
        </p>

        {/* Bevestiging e-mail */}
        <div className="bg-[#111] border border-[#C9A84C]/20 rounded-2xl p-5 text-left mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={14} className="text-[#C9A84C]" />
            <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Bevestiging verstuurd</span>
          </div>
          <p className="text-zinc-400 text-sm mb-4">
            Je ontvangt een bevestigingsmail op <strong className="text-white">{form.email || order?.email}</strong>.
          </p>
          <div className="space-y-3">
            {[
              "We analyseren je intake en stellen je schema samen",
              "Je ontvangt een e-mail zodra je schema klaar is",
              "Log in op je account en start direct met trainen",
            ].map((stap, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[#C9A84C] text-xs font-bold">{i + 1}</span>
                </span>
                <p className="text-zinc-500 text-xs leading-relaxed">{stap}</p>
              </div>
            ))}
          </div>
        </div>

        <button onClick={() => navigate("/")} className="btn-gold px-8 py-3 font-bold text-sm uppercase tracking-wide w-full">
          Terug naar home
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white">
                <ArrowLeft size={15} />
              </button>
            )}
            <div>
              <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Intakeformulier</p>
              <p className="text-white font-bold text-sm">{STEPS[step]}</p>
            </div>
          </div>
          <span className="text-zinc-600 text-xs font-bold">{step + 1} / {STEPS.length}</span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        <ProgressBar current={step + 1} total={STEPS.length} />

        {/* ── STAP 1: Persoonlijke gegevens ── */}
        {step === 0 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-white font-bold text-xl mb-6">Persoonlijke gegevens</h2>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Voornaam" required error={errors.voornaam}>
                <input value={form.voornaam} onChange={e => set("voornaam", e.target.value)} placeholder="Jan" data-testid="intake-voornaam" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
              </InputField>
              <InputField label="Achternaam" required error={errors.achternaam}>
                <input value={form.achternaam} onChange={e => set("achternaam", e.target.value)} placeholder="de Vries" data-testid="intake-achternaam" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
              </InputField>
            </div>
            <InputField label="Telefoonnummer" error={errors.telefoon}>
              <input type="tel" value={form.telefoon} onChange={e => set("telefoon", e.target.value)} placeholder="06-12345678" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
            </InputField>
            <InputField label="Geboortedatum" required error={errors.geboortedatum}>
              <input type="date" value={form.geboortedatum} onChange={e => set("geboortedatum", e.target.value)} data-testid="intake-geboortedatum" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
            </InputField>
            <InputField label="Geslacht" required error={errors.geslacht}>
              <select value={form.geslacht} onChange={e => set("geslacht", e.target.value)} data-testid="intake-geslacht" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
                <option value="">Kies...</option>
                <option value="man">Man</option>
                <option value="vrouw">Vrouw</option>
                <option value="anders">Anders</option>
                <option value="zeg_ik_niet">Wil ik niet zeggen</option>
              </select>
            </InputField>
          </div>
        )}

        {/* ── STAP 2: Lichaamsgegevens ── */}
        {step === 1 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-white font-bold text-xl mb-6">Lichaamsgegevens</h2>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Gewicht (kg)" required error={errors.gewicht}>
                <input type="number" min="30" max="250" value={form.gewicht} onChange={e => set("gewicht", e.target.value)} placeholder="75" data-testid="intake-gewicht" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
              </InputField>
              <InputField label="Lengte (cm)" required error={errors.lengte}>
                <input type="number" min="140" max="220" value={form.lengte} onChange={e => set("lengte", e.target.value)} placeholder="180" data-testid="intake-lengte" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
              </InputField>
            </div>
            <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
              <p className="text-zinc-500 text-xs leading-relaxed">Deze gegevens worden gebruikt om je trainingsschema beter af te stemmen op jouw lichaam en doelen.</p>
            </div>
          </div>
        )}

        {/* ── STAP 3: Doel en planning ── */}
        {step === 2 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-white font-bold text-xl mb-6">Doel & planning</h2>
            <InputField label="Trainingsdoel" required error={errors.doel}>
              <div className="grid grid-cols-2 gap-2">
                {[["afvallen", "Afvallen"], ["spieropbouw", "Spieropbouw"], ["onderhoud", "Onderhoud"], ["kracht", "Kracht"]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => set("doel", val)} data-testid={`doel-${val}`}
                    className={`py-3 rounded-xl border text-sm font-bold transition-all ${form.doel === val ? "bg-[#C9A84C]/15 border-[#C9A84C] text-[#C9A84C]" : "border-white/8 bg-[#1a1a1a] text-zinc-400 hover:border-[#C9A84C]/30"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <FieldError msg={errors.doel} />
            </InputField>
            <InputField label="Trainingsdagen per week" required error={errors.trainingsdagen}>
              <div className="flex gap-2">
                {[2, 3, 4, 5, 6].map(n => (
                  <button key={n} type="button" onClick={() => set("trainingsdagen", n)}
                    className={`flex-1 py-3 rounded-xl border text-sm font-bold transition-all ${form.trainingsdagen === n ? "bg-[#C9A84C]/15 border-[#C9A84C] text-[#C9A84C]" : "border-white/8 bg-[#1a1a1a] text-zinc-400"}`}>
                    {n}x
                  </button>
                ))}
              </div>
            </InputField>
            <InputField label="Werk-/weekschema" error={errors.weekschema}>
              <input value={form.weekschema} onChange={e => set("weekschema", e.target.value)} placeholder="bijv. werkend van 9-17, vrije dagen zaterdag/zondag" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
            </InputField>
            <InputField label="Sportschool" error={errors.sportschool}>
              <input value={form.sportschool} onChange={e => set("sportschool", e.target.value)} placeholder="bijv. Basic-Fit Amsterdam, thuis of eigen gym" data-testid="intake-sportschool" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
            </InputField>
            <InputField label="Specifiek doel" error={errors.specifiek_doel}>
              <textarea
                value={form.specifiek_doel}
                onChange={e => set("specifiek_doel", e.target.value)}
                placeholder="Bijv. ik wil meedoen aan een Hyrox-race over 3 maanden, ik wil 15 kg afvallen voor mijn bruiloft, of ik wil mijn squat naar 100 kg brengen..."
                rows={3}
                data-testid="intake-specifiek-doel"
                className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors resize-none"
              />
            </InputField>
            <InputField label="Trainingservaring" required error={errors.ervaring}>
              <div className="grid grid-cols-3 gap-2">
                {[["beginner", "Beginner"], ["gemiddeld", "Gemiddeld"], ["gevorderd", "Gevorderd"]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => set("ervaring", val)} data-testid={`ervaring-${val}`}
                    className={`py-3 rounded-xl border text-sm font-bold transition-all ${form.ervaring === val ? "bg-[#C9A84C]/15 border-[#C9A84C] text-[#C9A84C]" : "border-white/8 bg-[#1a1a1a] text-zinc-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <FieldError msg={errors.ervaring} />
            </InputField>
          </div>
        )}

        {/* ── STAP 4: Gezondheid ── */}
        {step === 3 && (
          <div className="space-y-5 animate-fadeIn">
            <h2 className="text-white font-bold text-xl mb-2">Gezondheid & bijzonderheden</h2>
            <p className="text-zinc-500 text-sm mb-6">Vul in wat van toepassing is. Mag ook "geen" zijn.</p>
            <InputField label="Fysieke klachten of blessures" error={errors.klachten}>
              <textarea value={form.klachten} onChange={e => set("klachten", e.target.value)} placeholder="bijv. knieklachten rechts, rugproblemen..." rows={3} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors resize-none" />
            </InputField>
            <InputField label="Mentale aandachtspunten" error={errors.mentaal}>
              <textarea value={form.mentaal} onChange={e => set("mentaal", e.target.value)} placeholder="bijv. stress, burn-out herstel, of geen..." rows={2} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors resize-none" />
            </InputField>
            <InputField label="Medicatie of bijzonderheden" error={errors.medicatie}>
              <textarea value={form.medicatie} onChange={e => set("medicatie", e.target.value)} placeholder="bijv. betablokkerd, diabetes, of geen..." rows={2} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors resize-none" />
            </InputField>
            <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-4">
              <p className="text-zinc-500 text-xs leading-relaxed">FitSmith geeft geen medisch advies. Bij twijfel, raadpleeg altijd een arts voordat je begint met trainen.</p>
            </div>
          </div>
        )}

        {/* ── STAP 5: Toestemming ── */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-white font-bold text-xl mb-6">Toestemming</h2>
            <div className="space-y-4">
              {[
                { key: "toestemming_data", text: "Ik geef toestemming dat FitSmith mijn gegevens gebruikt voor het opstellen van een persoonlijk trainingsschema." },
                { key: "toestemming_medisch", text: "Ik begrijp dat FitSmith geen medisch advies geeft en dat ik bij twijfel een arts raadpleeg." },
              ].map(({ key, text }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer bg-[#0f0f0f] border border-white/6 rounded-xl p-4 hover:border-[#C9A84C]/25 transition-colors">
                  <div
                    onClick={() => set(key, !form[key])}
                    data-testid={`toestemming-${key}`}
                    className={`w-5 h-5 rounded-md border shrink-0 mt-0.5 flex items-center justify-center transition-all ${form[key] ? "bg-[#C9A84C] border-[#C9A84C]" : "border-white/25 bg-[#1a1a1a]"}`}
                  >
                    {form[key] && <CheckCircle size={12} className="text-black" />}
                  </div>
                  <span className="text-zinc-300 text-sm leading-relaxed">{text}</span>
                </label>
              ))}
            </div>
            {(errors.toestemming_data || errors.toestemming_medisch) && (
              <div className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle size={12} /> Beide toestemmingen zijn verplicht
              </div>
            )}
            {errors.submit && (
              <div className="bg-red-500/10 border border-red-500/25 text-red-400 text-sm rounded-xl p-4">{errors.submit}</div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 flex gap-3">
          {step < STEPS.length - 1 ? (
            <button onClick={handleNext} data-testid="intake-next-btn"
              className="flex-1 btn-gold py-4 font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2">
              Volgende <ArrowRight size={15} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} data-testid="intake-submit-btn"
              className="flex-1 btn-gold py-4 font-bold text-sm uppercase tracking-wide disabled:opacity-60">
              {submitting ? "Versturen..." : "Intake indienen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
